import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useDialog } from "@/context/DialogProvider";
import {
  GraduationCap, Plus, Pencil, Trash2, AlertTriangle, CheckCircle2,
  Clock, X, ClipboardCheck, ChevronRight,
} from "lucide-react";

// VB-P-072 F02 staff training records.
//   Level 1 = CT  — Completed Training
//   Level 2 = API — Able to Perform Independently
//   Level 3 = ATC — Approved To Coach others
const COMPETENCY = {
  1: { code: "CT",  label: "Completed Training" },
  2: { code: "API", label: "Able to Perform Independently" },
  3: { code: "ATC", label: "Approved To Coach others" },
};

const blankForm = {
  title: "", trainer_name: "", training_date: "", competency_level: 1,
  retrain_date: "", remarks: "", evaluation_result: "pass", source: "ojt",
};

export default function TrainingTab({ targetId, canEdit, isArchived }) {
  const { confirm } = useDialog();
  const { user } = useAuth();
  // Supervisor+ can click the induction banner to open/edit. Staff (including
  // self-service view of their own profile) sees status only — sign-off is a
  // supervisor responsibility.
  const canOpenInduction = ["owner", "manager", "supervisor"].includes(user?.role);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [modal, setModal] = useState(null); // { mode: "add" | "edit", form, id? }

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/training/users/${targetId}`);
      setRecords(data.records || []);
    } catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed to load training"); }
    finally { setLoading(false); }
  };

  useEffect(() => { if (targetId) load(); /* eslint-disable-line */ }, [targetId]);

  const onAdd = () => setModal({ mode: "add", form: { ...blankForm }, id: null });
  const onEdit = (r) => setModal({
    mode: "edit", id: r.id,
    form: {
      title: r.title || "", trainer_name: r.trainer_name || "",
      training_date: r.training_date || "", competency_level: r.competency_level || 1,
      retrain_date: r.retrain_date || "", remarks: r.remarks || "",
      evaluation_result: r.evaluation_result || "pass", source: r.source || "ojt",
    },
  });

  const onDelete = async (r) => {
    if (!await confirm({
      title: `Delete training record?`,
      message: `"${r.title}" (${r.training_date}). This cannot be undone — for ISO 22000 retention, prefer keeping the row and updating fields.`,
      danger: true, confirmLabel: "Delete record",
    })) return;
    try { await api.delete(`/training/${r.id}`); await load(); }
    catch (e) { setErr(formatErr(e.response?.data?.detail) || "Failed to delete"); }
  };

  const counts = useMemo(() => {
    const c = { total: records.length, overdue: 0, dueSoon: 0 };
    for (const r of records) {
      if (r.retrain_status === "overdue") c.overdue++;
      else if (r.retrain_status === "due_soon") c.dueSoon++;
    }
    return c;
  }, [records]);

  if (loading) return <div className="vb-card p-6 opacity-70" data-testid="training-loading">Loading training…</div>;

  return (
    <div className="vb-card p-5" data-testid="training-tab">
      <div className="flex items-start justify-between gap-3 flex-wrap mb-4">
        <div>
          <h2 className="vb-heading text-xl m-0 mb-1" style={{ color: "var(--vb-text-2)" }}>
            <GraduationCap size={18} className="inline-block mr-2 -mt-1" />
            Training history
          </h2>
          <p className="text-xs opacity-60 m-0">
            VB-P-072 staff training record · {counts.total} record{counts.total === 1 ? "" : "s"}
            {counts.overdue > 0 && <span className="text-[11px] ml-2" style={{ color: "var(--vb-error)" }}>· {counts.overdue} overdue retrain{counts.overdue === 1 ? "" : "s"}</span>}
            {counts.dueSoon > 0 && <span className="text-[11px] ml-2" style={{ color: "var(--vb-warning, #C58B3B)" }}>· {counts.dueSoon} due soon</span>}
          </p>
        </div>
        {canEdit && !isArchived && (
          <button className="vb-btn" onClick={onAdd} data-testid="add-training-btn"><Plus size={14} /> Add training</button>
        )}
      </div>

      {err && <div className="vb-pill err mb-3" data-testid="training-error">{err}</div>}

      <InductionBanner targetId={targetId} canOpen={canOpenInduction} />

      {records.length === 0 ? (
        <div className="text-center py-8 opacity-60" data-testid="training-empty">
          <GraduationCap size={36} className="mx-auto opacity-40 mb-3" />
          <p className="text-sm mb-1">No training records yet.</p>
          <p className="text-xs">Induction, internal briefings, certifications and post-evaluations land here.</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="training-list">
          {records.map((r) => (
            <TrainingRow
              key={r.id} record={r}
              canEdit={canEdit && !isArchived}
              onEdit={() => onEdit(r)} onDelete={() => onDelete(r)}
            />
          ))}
        </div>
      )}

      {modal && (
        <TrainingModal
          targetId={targetId}
          mode={modal.mode} initialForm={modal.form} recordId={modal.id}
          onClose={() => setModal(null)}
          onSaved={() => { setModal(null); load(); }}
        />
      )}
    </div>
  );
}

function TrainingRow({ record: r, canEdit, onEdit, onDelete }) {
  const c = COMPETENCY[r.competency_level] || COMPETENCY[1];
  return (
    <div className="vb-card vb-training-row p-3 border" style={{ background: "var(--vb-bg)" }} data-testid={`training-row-${r.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-[260px]">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <strong className="text-sm">{r.title}</strong>
            <span
              className="vb-pill text-[10px] font-mono"
              title={c.label}
              data-testid={`training-competency-${r.id}`}
              style={{ background: "rgba(123, 74, 45, 0.12)" }}
            >
              {c.code}
            </span>
            <EvalBadge result={r.evaluation_result} />
            {r.session_id && (
              <span className="vb-pill text-[10px]" data-testid={`training-via-session-${r.id}`}
                title="Auto-created when a hosted training session was completed in Training & Coms"
                style={{ background: "rgba(46, 99, 12, 0.12)", color: "var(--vb-accent)" }}>
                via Session
              </span>
            )}
          </div>
          <div className="text-xs opacity-70 flex items-center gap-3 flex-wrap">
            {r.trainer_name && <span>Trainer: <strong className="opacity-90">{r.trainer_name}</strong></span>}
            <span>Date: {fmtDate(r.training_date)}</span>
            {r.retrain_date && <RetrainPill record={r} />}
          </div>
          {r.remarks && (
            <p className="text-xs opacity-70 mt-2 mb-0" style={{ borderLeft: "2px solid rgba(123, 74, 45, 0.18)", paddingLeft: 8 }}>
              {r.remarks}
            </p>
          )}
        </div>
        {canEdit && !r.session_id && (
          <div className="flex gap-1">
            <button className="vb-btn ghost" onClick={onEdit} data-testid={`edit-training-${r.id}`} title="Edit"><Pencil size={13} /></button>
            <button className="vb-btn ghost" onClick={onDelete} data-testid={`delete-training-${r.id}`} title="Delete"><Trash2 size={13} /></button>
          </div>
        )}
        {canEdit && r.session_id && (
          <div className="text-[10px] opacity-50" title="Edit via the source training session" data-testid={`session-locked-${r.id}`}>
            Managed by session
          </div>
        )}
      </div>
    </div>
  );
}

// Surface induction (F03) status at the top of every member's Training tab.
// Reuses /training/induction/users/{id} (lazy-creates the record on first
// load — that's fine, it just materialises the default checklist).
function InductionBanner({ targetId, canOpen }) {
  const [rec, setRec] = useState(null);
  useEffect(() => {
    if (!targetId) return;
    let mounted = true;
    api.get(`/training/induction/users/${targetId}`)
      .then(({ data }) => { if (mounted) setRec(data.record); })
      .catch(() => { /* staff viewing someone else's profile → 403 is fine */ });
    return () => { mounted = false; };
  }, [targetId]);

  if (!rec) return null;
  const status = rec.progress?.status;
  const done = rec.progress?.done ?? 0;
  const total = rec.progress?.total ?? 0;

  const variants = {
    signed_off:                { bg: "rgba(46, 99, 12, 0.10)",  border: "var(--vb-accent)",  Icon: CheckCircle2, label: "Induction signed off",
                                 sub: `Signed off ${fmtDate(rec.supervisor_signed_at)}`, pill: { cls: "ok", text: "Completed" } },
    complete_awaiting_signoff: { bg: "rgba(249, 115, 22, 0.10)", border: "var(--vb-warning, #C58B3B)", Icon: AlertTriangle, label: "Induction awaiting sign-off",
                                 sub: `${done}/${total} topics complete — supervisor sign-off required`, pill: { cls: "warn", text: "Awaiting sign-off" } },
    in_progress:               { bg: "rgba(123, 74, 45, 0.08)",  border: "var(--vb-text-2)",  Icon: Clock, label: "Induction in progress",
                                 sub: `${done}/${total} topics complete`, pill: { cls: "", text: "In progress" } },
    not_started:               { bg: "rgba(0, 0, 0, 0.04)",      border: "rgba(0,0,0,0.18)",  Icon: ClipboardCheck, label: "Induction not started",
                                 sub: `0/${total} topics complete`, pill: { cls: "", text: "Not started" } },
  };
  const v = variants[status] || variants.not_started;
  const Icon = v.Icon;

  const inner = (
    <div className="flex items-center gap-3" data-testid={`induction-banner-${targetId}`}>
      <Icon size={18} style={{ color: v.border, flexShrink: 0 }} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-semibold" style={{ color: "var(--vb-text-2)" }}>{v.label}</span>
          <span className={`vb-pill ${v.pill.cls} text-[10px]`} data-testid={`induction-pill-${status}`}>{v.pill.text}</span>
        </div>
        <div className="text-xs opacity-70">{v.sub}</div>
      </div>
      {canOpen && <ChevronRight size={14} className="opacity-60" />}
    </div>
  );

  const wrapperStyle = {
    background: v.bg,
    borderLeft: `3px solid ${v.border}`,
    borderRadius: 8,
    padding: "10px 14px",
    marginBottom: 16,
    display: "block",
  };

  if (canOpen) {
    return (
      <Link
        to={`/app/training-coms?tab=induction&user=${targetId}`}
        style={{ ...wrapperStyle, textDecoration: "none", color: "inherit" }}
        data-testid={`induction-banner-link-${targetId}`}
      >
        {inner}
      </Link>
    );
  }
  // Staff (viewing own profile) — read-only, no link.
  return (
    <div style={wrapperStyle} data-testid={`induction-banner-static-${targetId}`}>
      {inner}
    </div>
  );
}


function EvalBadge({ result }) {
  if (!result || result === "pending") {
    return <span className="vb-pill warn text-[10px]" data-testid="eval-pending"><Clock size={10} /> Pending</span>;
  }
  if (result === "fail") {
    return <span className="vb-pill err text-[10px]" data-testid="eval-fail"><X size={10} /> Failed</span>;
  }
  return <span className="vb-pill ok text-[10px]" data-testid="eval-pass"><CheckCircle2 size={10} /> Pass</span>;
}

function RetrainPill({ record: r }) {
  const days = r.retrain_days;
  if (r.retrain_status === "overdue") {
    return <span className="vb-pill err text-[10px]" data-testid="retrain-overdue">
      <AlertTriangle size={10} /> Retrain {Math.abs(days)}d overdue ({fmtDate(r.retrain_date)})
    </span>;
  }
  if (r.retrain_status === "due_soon") {
    return <span className="vb-pill warn text-[10px]" data-testid="retrain-due-soon">
      <Clock size={10} /> Retrain in {days}d ({fmtDate(r.retrain_date)})
    </span>;
  }
  return <span className="opacity-60 text-[11px]">Retrain: {fmtDate(r.retrain_date)}</span>;
}

function TrainingModal({ targetId, mode, initialForm, recordId, onClose, onSaved }) {
  const [form, setForm] = useState(initialForm);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      const payload = { ...form };
      // Drop empty optionals so server treats as null.
      if (!payload.retrain_date) payload.retrain_date = null;
      if (mode === "add") {
        await api.post(`/training/users/${targetId}`, payload);
      } else {
        await api.put(`/training/${recordId}`, payload);
      }
      onSaved();
    } catch (ex) {
      setErr(formatErr(ex.response?.data?.detail) || "Save failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="vb-modal-bg" onClick={onClose} data-testid="training-modal-backdrop">
      <div className="vb-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 560 }} data-testid="training-modal">
        <div className="flex items-start justify-between mb-3">
          <h2 className="vb-heading text-xl m-0">{mode === "add" ? "Add training record" : "Edit training record"}</h2>
          <button className="vb-btn ghost" onClick={onClose}><X size={14} /></button>
        </div>
        {err && <div className="vb-pill err mb-3" data-testid="training-modal-error">{err}</div>}
        <form onSubmit={submit} className="space-y-3" data-testid="training-form">
          <div>
            <label className="text-xs opacity-70">Training title *</label>
            <input className="vb-input" required maxLength={200} value={form.title}
              onChange={(e) => set("title", e.target.value)}
              placeholder="e.g. ISO 22000:2018 Awareness, HACCP Refresher"
              data-testid="training-title" />
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs opacity-70">Trainer</label>
              <input className="vb-input" value={form.trainer_name}
                onChange={(e) => set("trainer_name", e.target.value)}
                placeholder="Name of trainer" data-testid="training-trainer" />
            </div>
            <div>
              <label className="text-xs opacity-70">Training date *</label>
              <input className="vb-input" type="date" required value={form.training_date}
                onChange={(e) => set("training_date", e.target.value)}
                data-testid="training-date" />
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs opacity-70">Competency level *</label>
              <select className="vb-input" value={form.competency_level}
                onChange={(e) => set("competency_level", Number(e.target.value))}
                data-testid="training-competency">
                {[1, 2, 3].map((lvl) => (
                  <option key={lvl} value={lvl}>
                    {lvl} — {COMPETENCY[lvl].code} · {COMPETENCY[lvl].label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-xs opacity-70">Evaluation</label>
              <select className="vb-input" value={form.evaluation_result || "pass"}
                onChange={(e) => set("evaluation_result", e.target.value)}
                data-testid="training-eval">
                <option value="pass">Pass</option>
                <option value="fail">Failed</option>
                <option value="pending">Pending</option>
              </select>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs opacity-70">Retrain date <span className="opacity-60">(optional)</span></label>
              <input className="vb-input" type="date" value={form.retrain_date || ""}
                onChange={(e) => set("retrain_date", e.target.value)}
                data-testid="training-retrain-date" />
            </div>
            <div>
              <label className="text-xs opacity-70">Source</label>
              <select className="vb-input" value={form.source}
                onChange={(e) => set("source", e.target.value)}
                data-testid="training-source">
                <option value="ojt">On-the-job (internal)</option>
                <option value="session">Group session / briefing</option>
                <option value="external">External / certified</option>
              </select>
            </div>
          </div>
          <div>
            <label className="text-xs opacity-70">Remarks</label>
            <textarea className="vb-input" rows={2} maxLength={500} value={form.remarks}
              onChange={(e) => set("remarks", e.target.value)}
              placeholder="Optional notes — failures, follow-up actions, etc."
              data-testid="training-remarks" />
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" className="vb-btn ghost" onClick={onClose} data-testid="training-cancel">Cancel</button>
            <button type="submit" className="vb-btn" disabled={busy} data-testid="training-submit">
              {busy ? "Saving…" : mode === "add" ? "Add record" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const fmtDate = (d) => {
  if (!d) return "—";
  try { return new Date(d).toLocaleDateString(); }
  catch { return d; }
};
